import { useEffect } from "react";

const SITE_URL = "https://glenwoodgutterguy.com";
const DEFAULT_OG = `${SITE_URL}/og-image.jpg`;

function setMeta(attr, key, value) {
  let el = document.head.querySelector(`meta[${attr}="${key}"]`);
  if (!el) {
    el = document.createElement("meta");
    el.setAttribute(attr, key);
    document.head.appendChild(el);
  }
  el.setAttribute("content", value);
}

function setLink(rel, href) {
  let el = document.head.querySelector(`link[rel="${rel}"]`);
  if (!el) {
    el = document.createElement("link");
    el.setAttribute("rel", rel);
    document.head.appendChild(el);
  }
  el.setAttribute("href", href);
}

function upsertJsonLd(id, data) {
  let el = document.getElementById(id);
  if (!el) {
    el = document.createElement("script");
    el.type = "application/ld+json";
    el.id = id;
    document.head.appendChild(el);
  }
  el.textContent = JSON.stringify(data);
}

function removeJsonLd(id) {
  const el = document.getElementById(id);
  if (el) el.remove();
}

const DEFAULTS = {
  title: "Glenwood Gutter Guy — Gutters, Repairs & Screen Repair in SW Iowa",
  description:
    "Glenwood Gutter Guy — gutter cleaning, covers, repairs & resealing, and residential window screen replacement in Glenwood, Iowa and SW Iowa. 15% Military & First Responder discount. Text Gutters to (712) 310-9514.",
  ogImage: DEFAULT_OG,
  url: `${SITE_URL}/`,
};

/**
 * Per-page SEO. Pass null/undefined to reset to site defaults.
 */
export function useSeo({
  title,
  description,
  ogImage,
  url,
  type = "website",
  jsonLd,
} = {}) {
  useEffect(() => {
    const finalTitle = title || DEFAULTS.title;
    const finalDesc = description || DEFAULTS.description;
    const finalImage = ogImage || DEFAULTS.ogImage;
    const finalUrl = url || DEFAULTS.url;

    document.title = finalTitle;
    setMeta("name", "description", finalDesc);
    setLink("canonical", finalUrl);

    setMeta("property", "og:title", finalTitle);
    setMeta("property", "og:description", finalDesc);
    setMeta("property", "og:image", finalImage);
    setMeta("property", "og:image:secure_url", finalImage);
    setMeta("property", "og:url", finalUrl);
    setMeta("property", "og:type", type);

    setMeta("name", "twitter:title", finalTitle);
    setMeta("name", "twitter:description", finalDesc);
    setMeta("name", "twitter:image", finalImage);

    if (jsonLd) {
      upsertJsonLd("page-jsonld", jsonLd);
    } else {
      removeJsonLd("page-jsonld");
    }

    return () => {
      // On unmount, restore site defaults so SPA navigation never leaves stale tags
      document.title = DEFAULTS.title;
      setMeta("name", "description", DEFAULTS.description);
      setLink("canonical", DEFAULTS.url);
      setMeta("property", "og:title", DEFAULTS.title);
      setMeta("property", "og:description", DEFAULTS.description);
      setMeta("property", "og:image", DEFAULTS.ogImage);
      setMeta("property", "og:image:secure_url", DEFAULTS.ogImage);
      setMeta("property", "og:url", DEFAULTS.url);
      setMeta("property", "og:type", "website");
      setMeta("name", "twitter:title", DEFAULTS.title);
      setMeta("name", "twitter:description", DEFAULTS.description);
      setMeta("name", "twitter:image", DEFAULTS.ogImage);
      removeJsonLd("page-jsonld");
    };
  }, [title, description, ogImage, url, type, jsonLd]);
}

export const SITE = {
  url: SITE_URL,
  defaultOg: DEFAULT_OG,
};

// Canonical business identity used in LocalBusiness JSON-LD schema across the
// site. Update here if Mike ever moves, changes phone, or expands the service
// area — every schema-bearing page reads from this.
export const BUSINESS_INFO = {
  name: "Glenwood Gutter Guy",
  url: SITE_URL,
  telephone: "+1-712-310-9514",
  email: "mike.harrison@glenwoodgutterguyllc.com",
  image: `${SITE_URL}/logo-v2.png`,
  priceRange: "$$",
  address: {
    streetAddress: "901 Timber Ln",
    addressLocality: "Glenwood",
    addressRegion: "IA",
    postalCode: "51534",
    addressCountry: "US",
  },
  geo: {
    latitude: 41.0464,
    longitude: -95.7470,
  },
  areaServed: [
    "Glenwood, IA",
    "Pacific Junction, IA",
    "Malvern, IA",
    "Silver City, IA",
    "Mineola, IA",
    "Hastings, IA",
    "Tabor, IA",
    "Stanton, IA",
    "Shenandoah, IA",
    "Essex, IA",
    "Council Bluffs, IA",
    "Logan, IA",
    "Woodbine, IA",
    "Dunlap, IA",
    "Emerson, IA",
    "Red Oak, IA",
    "New Market, IA",
    "Bedford, IA",
  ],
};

/**
 * Build a LocalBusiness JSON-LD node — embed inside a parent BlogPosting/Blog
 * `publisher` field, or use directly as the page-level schema.
 */
export function localBusinessSchema() {
  return {
    "@type": "LocalBusiness",
    name: BUSINESS_INFO.name,
    url: BUSINESS_INFO.url,
    telephone: BUSINESS_INFO.telephone,
    email: BUSINESS_INFO.email,
    image: BUSINESS_INFO.image,
    priceRange: BUSINESS_INFO.priceRange,
    address: {
      "@type": "PostalAddress",
      ...BUSINESS_INFO.address,
    },
    geo: {
      "@type": "GeoCoordinates",
      ...BUSINESS_INFO.geo,
    },
    areaServed: BUSINESS_INFO.areaServed.map((a) => ({
      "@type": "City",
      name: a.split(",")[0],
    })),
  };
}
